import { ReactNode, useCallback } from "react";
import BaseButton from "src/components/BaseButton";
import Spinner from "src/components/Spinner";
import useIntersectionObserver from "src/pages/hooks/intersection-observer";

type HistoryInfiniteScrollFooterProps = {
  hasNextPage: boolean;
  isLoadingNextPage: boolean;
  disabled?: boolean;
  onLoadNextPage: () => void;
  // 다음 페이지 실패 시 목록 하단 인라인 표시용 (넘기지 않으면 기존 동작)
  isLoadError?: boolean;
  errorMessage?: ReactNode;
  retryLabel?: string;
  onRetry?: () => void;
};

const HistoryInfiniteScrollFooter = (props: HistoryInfiniteScrollFooterProps) => {
  const { hasNextPage, isLoadingNextPage, disabled, onLoadNextPage, isLoadError = false, errorMessage, retryLabel, onRetry } = props;
  const isEndPage = !hasNextPage && !isLoadingNextPage && !isLoadError;

  const onIntersect: IntersectionObserverCallback = useCallback(
    ([entry]: IntersectionObserverEntry[]) => {
      if (entry.isIntersecting && hasNextPage && !isLoadingNextPage && !disabled && !isLoadError) {
        onLoadNextPage();
      }
    },
    [disabled, hasNextPage, isLoadError, isLoadingNextPage, onLoadNextPage],
  );

  const { setTarget } = useIntersectionObserver({
    onIntersect,
    rootMargin: "160px 0px",
    threshold: [0],
  });

  // 실패 상태에서 sentinel을 그리면 자동 재요청이 반복되므로 재시도 버튼으로만 다시 요청
  if (isLoadError) {
    return (
      <div className="history-infinite-scroll-footer">
        <div className="history-infinite-scroll-footer__error">
          <span className="history-infinite-scroll-footer__error-message">{errorMessage}</span>
          {onRetry && <BaseButton title={retryLabel} className="base-btn color-white size-small" onClick={onRetry} />}
        </div>
      </div>
    );
  }

  return (
    <div className={`history-infinite-scroll-footer ${isEndPage ? "history-infinite-scroll-footer--end" : ""}`.trim()}>
      {hasNextPage && !isLoadingNextPage && !disabled && (
        <div ref={setTarget} className="history-infinite-scroll-footer__sentinel" aria-hidden="true" />
      )}
      {isLoadingNextPage && <Spinner size="md" />}
    </div>
  );
};

export default HistoryInfiniteScrollFooter;
